/**
 * What changed between two versions of a memo (DOP-621), section by
 * section, word by word.
 *
 * Both versions are read the way the reader sees them: prose with a
 * person's edits applied, the financial summary as its figures, and the
 * questions as a numbered list. So a re-run that moved EBITDA, and a person
 * who rewrote a risk, both show up as changes. The comparison is words, not
 * characters. "$9.1M" becoming "$10.4M" reads as one number swapped, not
 * four digits.
 */

import { stripMarks } from "./inlineMarks";
import { diffArrays } from "diff";

import type {
	DealScreeningFact,
	DealScreeningParagraph,
	DealScreeningRun,
	DealScreeningSection,
} from "../../types/dealScreening";
import { chartText } from "./chartModel";
import { formatFactValue } from "./dealScreeningFormat";
import { QUESTIONS_SECTION_KEY, companyFactsTitle, memoSections } from "./memoModel";

export interface DiffPart {
	text: string;
	kind: "same" | "added" | "removed";
}

export interface SectionDiff {
	key: string;
	title: string;
	parts: DiffPart[];
	/** Separate runs of changed words: "3 changes". */
	changes: number;
}

/** A paragraph as one line: its bold lead-in, then its sentences. */
const paragraphLine = (paragraph: DealScreeningParagraph): string => {
	if (paragraph.kind === "chart" && paragraph.chart) return `Chart: ${chartText(paragraph.chart)}`;
	if (paragraph.kind === "table" && paragraph.table) {
		const rows = paragraph.table.rows.map((row) => row.map((cell) => cell.text).join(" | "));
		return [paragraph.table.caption ?? "", ...rows].filter(Boolean).join("\n");
	}
	const text = paragraph.sentences.map((sentence) => sentence.text).join(" ");
	return paragraph.lead ? `${paragraph.lead}. ${text}` : text;
};

const factLine = (fact: DealScreeningFact): string =>
	`${fact.label}${fact.period ? ` ${fact.period}` : ""}: ${formatFactValue(fact.value, fact.unit)}`;

/** A section's text as the reader sees it. */
export function sectionText(run: DealScreeningRun, section: DealScreeningSection): string {
	if (section.status === "omitted") return "";
	if (section.key === "financial_summary") {
		return run.facts
			.filter((fact) => fact.kind !== "header")
			.map(factLine)
			.join("\n");
	}
	if (section.key === QUESTIONS_SECTION_KEY && section.paragraphs.length === 0) {
		return [...run.questions]
			.sort((a, b) => a.sort_order - b.sort_order)
			.map((question, index) => `${index + 1}. ${question.topic}. ${question.text}`)
			.join("\n");
	}
	return section.paragraphs.map(paragraphLine).join("\n\n");
}

/** The header block as a pseudo-section, so a changed raise size shows up. */
const headerText = (run: DealScreeningRun): string =>
	run.facts
		.filter((fact) => fact.kind === "header")
		.map(factLine)
		.join("\n");

const countChanges = (parts: DiffPart[]): number => {
	let changes = 0;
	let inChange = false;
	for (const part of parts) {
		if (part.kind === "same") {
			// A space between two changed words does not split one change.
			if (part.text.trim() !== "") inChange = false;
		} else if (!inChange) {
			changes += 1;
			inChange = true;
		}
	}
	return changes;
};

/**
 * Words, each with the space after it, so a run of changed words is one
 * piece and punctuation stays with its word. Leading space is its own token.
 */
const tokens = (text: string): string[] => text.match(/^\s+|\S+\s*/g) ?? [];

/**
 * Two words are the same word whatever space follows them: a word typed
 * after the last one must not mark that one as changed because it now has a
 * space after it (DOP-641). A same part keeps the newer text's spacing.
 */
const sameWord = (a: string, b: string): boolean => a.trimEnd() === b.trimEnd();

export function diffText(before: string, after: string): DiffPart[] {
	return diffArrays(tokens(before), tokens(after), { comparator: sameWord }).map((change) => ({
		text: change.value.join(""),
		kind: change.added ? "added" : change.removed ? "removed" : "same",
	}));
}

/**
 * Every section of either version, in the newer one's order, each with its
 * word diff. A section only one version has reads as wholly added or removed.
 */
export function diffRuns(before: DealScreeningRun, after: DealScreeningRun): SectionDiff[] {
	const beforeSections = new Map(memoSections(before.sections).map((s) => [s.key, s]));
	const afterSections = memoSections(after.sections);
	const keys = [
		...afterSections.map((s) => s.key),
		...Array.from(beforeSections.keys()).filter(
			(key) => !afterSections.some((s) => s.key === key),
		),
	];
	const diffs: SectionDiff[] = [];
	const header = diffText(headerText(before), headerText(after));
	diffs.push({ key: "header", title: stripMarks(companyFactsTitle(after)), parts: header, changes: countChanges(header) });
	for (const key of keys) {
		const was = beforeSections.get(key);
		const now = afterSections.find((s) => s.key === key);
		const parts = diffText(
			was ? sectionText(before, was) : "",
			now ? sectionText(after, now) : "",
		);
		diffs.push({
			key,
			title: stripMarks((now ?? was)?.title ?? key),
			parts,
			changes: countChanges(parts),
		});
	}
	return diffs;
}
